import {Box, Grid, Header, Multiselect, Pagination, SpaceBetween, Table, TableProps} from '@amzn/awsui-components-react';
import React, {useState} from 'react';
import {useCollection} from '@amzn/awsui-collection-hooks';
import {InboundBridgeRecord, NodeFilterData} from './index';

const tableColumnDefinitions = (): TableProps.ColumnDefinition<any>[] => {
    return [
        {
            id: 'date',
            header: 'Date',
            cell: (item: InboundBridgeRecord) => item.date,
            isRowHeader: true,
        },
        {
            id: 'node',
            header: 'Node',
            cell: (item: InboundBridgeRecord) => item.node,
            isRowHeader: true,
        },
        {
            id: 'channel',
            header: 'Channel',
            cell: (item: InboundBridgeRecord) => item.granularity?.['channel'] || '-',
            isRowHeader: true,
        },
        {
            id: 'sourceLocation',
            header: 'Source',
            cell: (item: InboundBridgeRecord) => item.granularity?.['sourceLocation'] || '-',
            isRowHeader: true,
        },
        {
            id: 'ibType',
            header: 'Inbound Type',
            cell: (item: InboundBridgeRecord) => item.granularity?.['ibType'] || '-',
            isRowHeader: true,
        },
        {
            id: 'tempZone',
            header: 'Temp Zone',
            cell: (item: InboundBridgeRecord) => item.granularity?.['tempZone'] || '-',
            isRowHeader: true,
        },
        {
            id: 'bucketSeqId',
            header: 'Bucket Seq',
            cell: (item: InboundBridgeRecord) => item.bucketSeqId,
            isRowHeader: true,
        },
        {
            id: 'bucketName',
            header: 'Bucket',
            cell: (item: InboundBridgeRecord) => item.bucketName,
            isRowHeader: true,
        },
        {
            id: 'bucketValue',
            header: 'Running Total',
            cell: (item: InboundBridgeRecord) => item.bucketValue,
            isRowHeader: true,
        },
        {
            id: 'bucketError',
            header: 'Error',
            cell: (item: InboundBridgeRecord) => item.bucketError,
            isRowHeader: true,
        },
        {
            id: 'wapeContribution',
            header: 'WAPE Contribution',
            cell: (item: InboundBridgeRecord) => item.wapeContribution,
            isRowHeader: true,
        },
    ];
};

const TABLE_PAGE_SIZE = 50;

export function InboundBridgeTable({
    bridgeData,
    selectedFilter,
}: {
    bridgeData: InboundBridgeRecord[];
    selectedFilter: NodeFilterData | undefined;
}) {
    const [tableSelectedDates, setTableSelectedDates] = useState<string[]>([]);

    const tableEmpty = (
        <Box textAlign="center" color="inherit">
            <b>No matching data</b>
        </Box>
    );

    const {items, collectionProps, paginationProps} = useCollection(bridgeData, {
        filtering: {
            empty: tableEmpty,
            noMatch: tableEmpty,
            filteringFunction(item) {
                return tableSelectedDates.length == 0 || tableSelectedDates.includes(item.date);
            },
        },
        pagination: {pageSize: TABLE_PAGE_SIZE},
        sorting: {
            defaultState: {
                sortingColumn: {sortingField: 'date'},
                isDescending: false,
            },
        },
        selection: {
            keepSelection: true,
        },
    });

    return (
        <Table
            data-testid="page-table"
            {...collectionProps}
            stickyHeader
            stripedRows
            header={<Header>Inbound Bridge Data Details</Header>}
            items={items}
            pagination={<Pagination {...paginationProps} />}
            filter={
                <React.Fragment>
                    <Grid gridDefinition={[{colspan: 4}]}>
                        <Multiselect
                            data-testid="table-date-multiselect"
                            selectedOptions={tableSelectedDates.map((item) => ({label: item, value: item}))}
                            onChange={async ({detail}) => {
                                const dates = detail.selectedOptions.map((item) => item.value!);
                                setTableSelectedDates(dates);
                            }}
                            options={getSelectedDateList().map((item) => ({label: item, value: item}))}
                            tokenLimit={5}
                            filteringType="auto"
                            placeholder="Select Balance Dates"
                        />
                    </Grid>
                </React.Fragment>
            }
            columnDefinitions={tableColumnDefinitions()}
        />
    );

    function getSelectedDateList(): string[] {
        const startDate = selectedFilter?.balanceDateStart ?? '';
        const endDate = selectedFilter?.balanceDateEnd ?? '';
        const dates: string[] = [];
        const current = new Date(startDate);
        const end = new Date(endDate);

        while (current <= end) {
            dates.push(current.toISOString().split('T')[0]);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    }
}
